import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  type CreateNumberData,
  type UpdateNumberData,
  type Number,
} from "../services/numberApi";
import { type Company, companyApi } from "../services/companyApi";
import { numberSchema } from "@repo/schemas/numberSchema";

type NumberFormData = z.infer<typeof numberSchema>;

interface NumberFormProps {
  number?: Number;
  onSubmit: (data: CreateNumberData | UpdateNumberData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function NumberForm({
  number,
  onSubmit,
  onCancel,
  isLoading,
}: NumberFormProps) {
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = React.useState(true);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<NumberFormData>({
    resolver: zodResolver(numberSchema),
    defaultValues: {
      e164Number: number?.e164Number || "",
      friendlyName: number?.friendlyName || "",
      country: number?.country || "",
      region: number?.region || "",
      locality: number?.locality || "",
      voiceUrl: number?.voiceUrl || "",
      source: number?.source || "twilio",
      companyId: number?.company?.id || null,
      externalId: number?.externalId || "",
      boughtAt: number?.boughtAt
        ? new Date(number.boughtAt).toISOString().split("T")[0]
        : "",
    },
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  // Reset form when number prop changes
  useEffect(() => {
    if (number) {
      reset({
        e164Number: number.e164Number,
        friendlyName: number.friendlyName,
        country: number.country,
        region: number.region || "",
        locality: number.locality || "",
        voiceUrl: number.voiceUrl || "",
        source: number.source,
        companyId: number.company?.id || null,
        boughtAt: number.boughtAt
          ? new Date(number.boughtAt).toISOString().split("T")[0]
          : "",
      });
    }
  }, [number, reset]);

  const loadCompanies = async () => {
    try {
      const fetchedCompanies = await companyApi.getAllCompanies();
      setCompanies(fetchedCompanies);
    } catch (error) {
      console.error("Failed to load companies:", error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const onFormSubmit = (data: NumberFormData) => {
    const submitData = {
      ...data,
      companyId: data.companyId === "none" ? null : data.companyId,
      voiceUrl: data.voiceUrl || undefined,
      region: data.region || undefined,
      locality: data.locality || undefined,
      boughtAt: data.boughtAt || undefined,
    };
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="e164Number">E.164 Phone Number *</Label>
          <Input
            id="e164Number"
            {...register("e164Number")}
            placeholder="+1234567890"
            disabled={!!number} // Don't allow editing phone number
          />
          {errors.e164Number && (
            <p className="text-sm text-red-600">{errors.e164Number.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="friendlyName">Friendly Name *</Label>
          <Input
            id="friendlyName"
            {...register("friendlyName")}
            placeholder="Main Support Line"
          />
          {errors.friendlyName && (
            <p className="text-sm text-red-600">
              {errors.friendlyName.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Country *</Label>
          <Input
            id="country"
            {...register("country")}
            placeholder="CA"
            maxLength={2}
          />
          {errors.country && (
            <p className="text-sm text-red-600">{errors.country.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="source">Source *</Label>
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                  <SelectItem value="telnyx">Telnyx</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {errors.source && (
            <p className="text-sm text-red-600">{errors.source.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="region">Region</Label>
          <Input id="region" {...register("region")} placeholder="Ontario" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="locality">Locality</Label>
          <Input
            id="locality"
            {...register("locality")}
            placeholder="Toronto"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="voiceUrl">Voice URL</Label>
          <Input
            id="voiceUrl"
            {...register("voiceUrl")}
            placeholder="https://example.com/twiml"
          />
          {errors.voiceUrl && (
            <p className="text-sm text-red-600">{errors.voiceUrl.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="boughtAt">Purchase Date</Label>
          <Input id="boughtAt" type="date" {...register("boughtAt")} />
        </div>
        <div className="space-y-2 col-span-2">
          <Label htmlFor="externalId">External ID</Label>
          <Input id="externalId" {...register("externalId")} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="companyId">Assigned Company</Label>
        <Controller
          name="companyId"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value || "none"}
              disabled={companiesLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Company</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {companiesLoading && (
          <p className="text-sm text-gray-500">Loading companies...</p>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : number ? "Update Number" : "Create Number"}
        </Button>
      </div>
    </form>
  );
}
